import React, { useMemo } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { SpeakersAnalysis } from '@goldcast/player-config/src/types/speakers-analysis';
import { timeStringToSeconds } from '@goldcast/player-config/src/utils/timeUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { findAdjustedBoxBoundaries } from '@goldcast/player-config/src/utils/speakersAnalysisUtils';
import FocusLayoutPreview from './FocusLayoutPreview';
import ClipInterimThumbnailCanvasPreview from './ClipInterimThumbnailCanvasPreview';
import AudiogramPreview from '../SideBar/AudiogramPreview';
import { SpeakerSegmentContext } from '@/context/SpeakerSegmentContext/SpeakerSegmentContext';
import { ClipsContext } from '@/context/ClipsContext/ClipsContext';

/**
 * Interim thumbnail for clips without a download config. Using the clip's asset metadata, this component
 * will render a preview of the clip. Once the clip's download config is available, this will be replaced
 * with the actual thumbnail.
 */
export default function ClipInterimThumbnail({
  clip,
  firstSpeaker,
  imageLoaded,
  clipThumbnailRef,
  captions,
  children
}: {
  clip: Clip;
  firstSpeaker?: SpeakerWithDetails;
  imageLoaded: boolean;
  clipThumbnailRef: React.RefObject<HTMLImageElement>;
  captions: Word[];
  children: React.ReactNode;
}) {
  const isUpload = useMemo(() => {
    return clip.content.media_source_type === 'UPLOAD';
  }, [clip.content.media_source_type]);

  const firstFrameSpeakerAnalysis: SpeakersAnalysis = useMemo(() => {
    if (!isUpload || !clip.first_frame_analysis) {
      return {
        face_ids: [],
        speaker_mapping: {},
        time_analysis: [],
        loading: false,
        times: [],
        visible_face_ids: [],
        accurate: false,
        adjustedBoxBoundaries: []
      };
    }

    const timeAnalysis = clip.first_frame_analysis.time_analysis || [];

    const adjustedBoxBoundaries = findAdjustedBoxBoundaries(timeAnalysis);

    return {
      face_ids: clip.first_frame_analysis.face_ids || [],
      speaker_mapping: clip.first_frame_analysis.speaker_mapping || {},
      time_analysis: timeAnalysis,
      loading: false,
      times: timeAnalysis?.map(t => roundToNDecimalPlaces(timeStringToSeconds(t.time) + clip.asset_metadata.start, 4)),
      visible_face_ids: clip.first_frame_analysis.face_ids?.slice(0, 4) || [],
      accurate: clip.first_frame_analysis.accurate || false,
      adjustedBoxBoundaries
    };
  }, [clip.first_frame_analysis, clip.asset_metadata.start, isUpload]);

  return (
    <div className="absolute top-0 size-full overflow-hidden rounded-md bg-cover bg-center">
      <div className="relative grid h-full items-start" data-testid="composite-player">
        {children}
        {firstSpeaker && clip.asset_metadata.layout === 'AUDIOGRAM' && (
          <ClipsContext.Provider
            value={
              {
                clipId: clip.id,
                clipData: clip,
                playerStore: {}
              } as any
            }
          >
            <SpeakerSegmentContext.Provider
              value={{
                captions,
                segmentSpeaker: { [firstSpeaker.key]: firstSpeaker },
                segmentWords: {},
                srtCaptions: { 0: captions },
                startSegment: 0
              }}
            >
              <AudiogramPreview clipTemplate={clip.asset_metadata} currentSpeaker={firstSpeaker} />
            </SpeakerSegmentContext.Provider>
          </ClipsContext.Provider>
        )}
        {imageLoaded &&
          !!clipThumbnailRef.current &&
          firstSpeaker &&
          ['SPEAKER', 'GRID', 'DEFAULT'].includes(clip.asset_metadata.layout) && (
            <ClipsContext.Provider
              value={
                {
                  clipId: clip.id,
                  clipData: clip,
                  speakersAnalysis: firstFrameSpeakerAnalysis,
                  playerStore: {}
                } as any
              }
            >
              <SpeakerSegmentContext.Provider
                value={{
                  captions,
                  segmentSpeaker: { [firstSpeaker.key]: firstSpeaker },
                  segmentWords: {},
                  srtCaptions: { 0: captions },
                  startSegment: 0
                }}
              >
                <ClipInterimThumbnailCanvasPreview
                  clip={clip}
                  clipThumbnailRef={clipThumbnailRef}
                  firstSpeaker={firstSpeaker}
                />
              </SpeakerSegmentContext.Provider>
            </ClipsContext.Provider>
          )}
        {imageLoaded && !!clipThumbnailRef.current && clip.asset_metadata.layout === 'FOCUS' && (
          <FocusLayoutPreview clip={clip} />
        )}
      </div>
    </div>
  );
}
